
import { supabase } from '@/integrations/supabase/client';
import { AuthUser } from '@/types/auth';
import { toast } from '@/components/ui/use-toast';
import { storeUser } from './userStorage';
import * as Sentry from "@sentry/react";

/**
 * Perform user login
 */
export const performLogin = async (email: string, password: string): Promise<AuthUser> => {
  // Set Sentry breadcrumb to track login attempt
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Login attempt',
    level: 'info',
  });
  
  // Simple validation
  if (!email || !password) {
    const error = new Error('Email and password are required');
    Sentry.captureException(error);
    throw error;
  }
  
  try {
    // Call Supabase auth API
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      // Capture authentication errors in Sentry
      Sentry.captureException(error, {
        tags: { auth_method: 'email_password', action: 'login' },
        contexts: {
          auth: { provider: 'supabase', email },
        },
      });
      throw new Error(error.message);
    }
    
    if (!data.user) {
      const noUserError = new Error('Login failed: No user returned');
      Sentry.captureException(noUserError);
      throw noUserError;
    }
    
    // Fetch user profile from profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('age_range, education_level, country')
      .eq('id', data.user.id)
      .single();
    
    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      Sentry.captureException(profileError, {
        tags: { action: 'fetch_profile' },
      });
    }
    
    // Fetch user roles
    const { data: rolesData, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', data.user.id);
    
    if (rolesError) {
      console.error('Error fetching user roles:', rolesError);
      Sentry.captureException(rolesError, {
        tags: { action: 'fetch_roles' },
      });
    }
    
    console.log("Fetched roles:", rolesData);
    
    // Create user object
    const user: AuthUser = {
      id: data.user.id,
      email: data.user.email || '',
      ageRange: profileData?.age_range || undefined,
      educationLevel: profileData?.education_level || undefined,
      country: profileData?.country || undefined,
      roles: rolesData?.map(r => r.role) || ['user'],
    };
    
    console.log("Created user object:", user);
    
    // Store user in localStorage
    storeUser(user);
    
    // Set user identifier in Sentry
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.email.split('@')[0],
    });
    
    // Add user roles to Sentry tags
    if (user.roles && user.roles.length > 0) {
      Sentry.setTag('user_role', user.roles.join(','));
    }
    
    // Add login success breadcrumb
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Login successful',
      level: 'info',
    });
    
    toast({
      title: "Success",
      description: "You have successfully logged in.",
    });
    
    return user;
  } catch (error) {
    // Ensure all errors are captured by Sentry
    if (!(error instanceof Error)) {
      const unknownError = new Error('Unknown login error');
      Sentry.captureException(unknownError);
      throw unknownError;
    }
    
    // Re-throw the original error after capturing
    throw error;
  }
};

/**
 * Send magic link for passwordless login
 */
export const sendMagicLink = async (email: string): Promise<void> => {
  // Set Sentry breadcrumb
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Magic link requested',
    level: 'info',
  });
  
  // Simple validation
  if (!email) {
    const error = new Error('Email is required');
    Sentry.captureException(error);
    throw error;
  }
  
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: window.location.origin,
      }
    });
    
    if (error) {
      console.error('Error sending magic link:', error);
      Sentry.captureException(error, {
        tags: { auth_method: 'magic_link' },
        contexts: {
          auth: { provider: 'supabase', email },
        },
      });
      throw new Error(error.message);
    }
    
    // Add successful magic link sent breadcrumb
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Magic link sent successfully',
      level: 'info',
    });
    
    toast({
      title: "Magic Link Sent",
      description: "Check your email for a login link.",
    });
  } catch (error) {
    // Ensure all errors are captured
    if (!(error instanceof Error)) {
      const unknownError = new Error('Unknown error sending magic link');
      Sentry.captureException(unknownError);
      throw unknownError;
    }
    
    // Re-throw the original error
    throw error;
  }
};
